import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save, X } from 'lucide-react';

interface SaveCalculationDialogProps {
  open: boolean;
  onClose: () => void;
  data: any;
  onSave: (name: string, data: any, description?: string) => Promise<void>;
  defaultName?: string;
  toolType?: string;
}

export function SaveCalculationDialog({
  open,
  onClose,
  data,
  onSave,
  defaultName = '',
  toolType = 'calculation',
}: SaveCalculationDialogProps) {
  const [projectName, setProjectName] = useState(defaultName);
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens with default values
  useEffect(() => {
    if (open) {
      setProjectName(defaultName);
      setDescription('');
      setError(null);
    }
  }, [open, defaultName]);

  // Pre-fill project name if data contains a project name
  useEffect(() => {
    if (open && data?.projectName && !projectName) {
      setProjectName(data.projectName);
    }
  }, [open, data, projectName]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!projectName.trim()) {
      setError('Project name is required');
      return;
    }
    
    setIsSaving(true);
    setError(null);
    
    try {
      await onSave(projectName.trim(), data, description.trim() || undefined);
      onClose();
    } catch (error) {
      console.error('Error saving calculation:', error);
      setError(error instanceof Error ? error.message : 'Failed to save calculation');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isSaving && !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Save className="h-5 w-5 mr-2 text-blue-500" />
            Save {toolType.charAt(0).toUpperCase() + toolType.slice(1)}
          </DialogTitle>
          <DialogDescription>
            Save your current work to access it later
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSave} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="projectName" className="text-sm font-medium">
              Project Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="projectName"
              value={projectName}
              onChange={(e) => {
                setProjectName(e.target.value);
                setError(null);
              }}
              placeholder="Enter project name"
              disabled={isSaving}
              className={error ? "border-red-300 focus:border-red-500" : ""}
              required
              autoFocus
            />
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">
              Description <span className="text-gray-400 text-xs">(optional)</span>
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a brief description..."
              disabled={isSaving}
              rows={3}
            />
          </div>
          
          <div className="bg-gray-50 -mx-6 px-6 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-2">
              This will save your current work and make it available in your saved {toolType}s list
            </p>
          </div>
          
          <DialogFooter className="sm:justify-between">
            <div className="flex gap-2 w-full justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSaving}
                className="flex items-center"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSaving || !projectName.trim()} 
                className="flex items-center"
              >
                {isSaving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}